import React, { useState, useEffect, useRef } from 'react';
import { HelpCircle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { getHelpContent, HelpContentItem } from '../../utils/helpContent';

interface HelpSidebarProps {
  children: React.ReactNode;
}

export const HelpSidebarLayout: React.FC<HelpSidebarProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonPosition, setButtonPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const location = useLocation();
  const sidebarRef = useRef<HTMLDivElement>(null);
  
  // Load saved state from localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('helpSidebarOpen');
    const savedPosition = localStorage.getItem('helpButtonPosition');
    
    if (savedState !== null) {
      setIsOpen(JSON.parse(savedState));
    }
    if (savedPosition !== null) {
      setButtonPosition(parseFloat(savedPosition));
    }
  }, []);
  
  // Save state to localStorage
  useEffect(() => {
    localStorage.setItem('helpSidebarOpen', JSON.stringify(isOpen));
  }, [isOpen]);
  
  useEffect(() => {
    localStorage.setItem('helpButtonPosition', buttonPosition.toString());
  }, [buttonPosition]);
  
  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const isHelpButton = target.closest('button[aria-label="Hilfe öffnen"]');
      
      if (sidebarRef.current && !sidebarRef.current.contains(target) && !isHelpButton) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      // Small delay to prevent immediate closing when opening
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);
  
  // Get content for current page
  const helpData = getHelpContent(location.pathname);
  
  // Handle drag functionality
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    
    const windowHeight = window.innerHeight;
    const newPosition = (e.clientY / windowHeight) * 100;
    
    // Constrain position between 10% and 90% of viewport height
    const constrainedPosition = Math.max(10, Math.min(90, newPosition));
    setButtonPosition(constrainedPosition);
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  // Add global mouse event listeners when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ns-resize';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = 'default';
      };
    }
  }, [isDragging]);
  
  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Main content area */}
      <div className={`flex-1 transition-all duration-300 ${isOpen ? 'mr-80' : 'mr-0'}`}>
        {children}
      </div>
      
      {/* Help button - always visible */}
      <div
        className={`fixed z-40 transition-all duration-300 hidden lg:block ${
          isOpen ? 'right-80' : 'right-0'
        }`}
        style={{ 
          top: `${buttonPosition}%`, 
          transform: 'translateY(-50%)',
          cursor: isDragging ? 'ns-resize' : 'default'
        }}
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          onMouseDown={handleMouseDown}
          className={`bg-blue-600 text-white p-2 rounded-l-lg shadow-lg hover:bg-blue-700 transition-colors flex items-center justify-center ${
            isDragging ? 'cursor-ns-resize' : 'cursor-pointer'
          }`}
          aria-label="Hilfe öffnen"
          title="Klicken zum Öffnen, Ziehen zum Verschieben"
        >
          <span className="text-lg font-medium">?</span>
        </button>
      </div>
      
      {/* Help sidebar */}
      <div
        ref={sidebarRef}
        className={`fixed right-0 top-0 h-full w-80 bg-white shadow-2xl transform transition-transform duration-300 overflow-hidden hidden lg:block ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ zIndex: 30 }}
      >
        {/* Header */}
        <div className="bg-blue-600 text-white p-4 flex items-center gap-2">
          <HelpCircle className="h-5 w-5" />
          <h2 className="text-lg font-semibold">Hilfe</h2>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto h-full pb-20">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">{helpData.title}</h3>
          
          {/* Intro text */}
          <p className="text-gray-700 mb-6 leading-relaxed">{helpData.content}</p>
          
          {/* Feature list */}
          {helpData.features && helpData.features.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-800 mb-3">{helpData.featuresTitle || 'Funktionen:'}</h4>
              {helpData.features.map((feature, index) => (
                <div key={index} className="flex items-start gap-2">
                  <span className="text-gray-500 mt-1">•</span>
                  <p className="flex-1 text-gray-700 leading-relaxed">{feature.text}</p>
                </div>
              ))}
            </div>
          )}
          
          {/* Additional sections */}
          {helpData.sections && helpData.sections.map((section, index) => (
            <div key={index} className="mt-6">
              <h4 className="font-semibold text-gray-800 mb-3">{section.title}</h4>
              <div className="space-y-2">
                {section.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex items-start gap-2">
                    <span className="text-gray-500 mt-1">{itemIndex + 1}.</span>
                    <p className="text-gray-700 leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          {/* Note */}
          {helpData.note && (
            <div className="mt-6 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">{helpData.note}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// For backwards compatibility
const HelpSidebar: React.FC = () => {
  return null; // This component is now integrated into HelpSidebarLayout
};

export default HelpSidebar;